import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Column } from 'react-table'
import { Button, Dropdown, message, Modal } from 'antd'
import type { MenuProps } from 'antd'
import { useBackgroundNavigate } from '@shared/hooks/use-background-navigate'
import * as Icons from '@ant-design/icons'

import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns'
import { StopPropagation } from '@shared/components/stop-propagation'
import { localURLMaker } from '@shared/utils/url-maker'
import { useSearchParams } from '@shared/hooks/use-search-params'

import { ProductCategory } from '../types/categories'
import { ProductCategoriesTableContext } from '../context'
import { ProductCategoriesService } from '../services/categories'
import { ProductCategoriesRoutes } from '../constants/categories'
import { useAuth } from '@modules/me/hooks'

export const useProductCategoriesTable = () => {
  const { handleFetch } = useContext(ProductCategoriesTableContext)
  const navigate = useBackgroundNavigate()
  const searchParams = useSearchParams<{ reFetchProductCategoriesTable?: string }>()
  const { can } = useAuth()

  const columns = useMemo<Column<ProductCategory>[]>(
    () => [
      {
        ...nextTableColumns.actions,
        Cell: ({ row: { original } }) => {
          const goTo = useCallback(
            (type: string) => {
              navigate(localURLMaker('/products/categories/:id/:type', { id: original.id, type }), { withBackground: true })
            },
            [original.id],
          )

          const remove = useCallback(() => {
            const action = async () => {
              const result = await ProductCategoriesService.actionByIds([original.id], 'remove')

              if (result.status === 200) {
                message.success('Əməliyyat müvəffəqiyyətlə başa çatdı.')
                navigate(localURLMaker(ProductCategoriesRoutes.Index, {}, { reFetchProductCategoriesTable: true }))
              } else {
                message.error(result.data)
              }
            }

            Modal.confirm({
              title: 'Silməyə əminsinizmi?',
              content: 'Silmə əməliyyatını təsdiqlədikdən sonra məlumatların geri qaytarılması mümkün olmayacaq.',
              onOk: action,
            })
          }, [original.id])

          const menuItems: MenuProps['items'] = [
            can(46) && {
              key: 'update',
              label: 'Dəyişdir',
              icon: <Icons.EditOutlined />,
              onClick: () => goTo('update'),
            },
            { type: 'divider' },
            can(47) && {
              key: 'remove',
              label: 'Sil',
              icon: <Icons.DeleteOutlined />,
              onClick: remove,
            },
          ].filter(Boolean) as MenuProps['items']

          return (
            <StopPropagation>
              <Dropdown menu={{ items: menuItems }}>
                <Button size='small' icon={<Icons.MoreOutlined />} />
              </Dropdown>
            </StopPropagation>
          )
        },
      },
      {
        ...nextTableColumns.smaller,
        accessor: (row) => row.id,
        id: 'id',
        Header: 'Kod',
      },
      {
        accessor: (row) => row.name,
        id: 'product_category_name',
        Header: 'Ad',
      },
      {
        accessor: (row) => row.description,
        id: 'descr',
        Header: 'Açıqlama',
      },
    ],
    [can, navigate],
  )

  useEffect(() => {
    (async () => {
      if (searchParams.searchParams.reFetchProductCategoriesTable) {
        searchParams.remove.current('reFetchProductCategoriesTable')
        await handleFetch()
      }
    })()
  }, [searchParams.searchParams.reFetchProductCategoriesTable, searchParams.remove, handleFetch])

  return { columns }
}
